// Product pages fetched for the quick-add sheet, keyed by URL. Shared by prefetching (hover, focus,
// touch) and real loads so the sheet and option switches reuse a response instead of refetching it.
const RESPONSE_CACHE = new Map();
const RESPONSE_CACHE_LIMIT = 24;

function fetchProductHtml(url) {
  const cached = RESPONSE_CACHE.get(url);
  if (cached) return cached;

  const request = fetch(url, { credentials: 'same-origin' }).then((response) => {
    if (!response.ok) throw new Error(`Failed to load quick add product: ${response.status}`);
    return response.text();
  });
  request.catch(() => RESPONSE_CACHE.delete(url));

  RESPONSE_CACHE.set(url, request);
  if (RESPONSE_CACHE.size > RESPONSE_CACHE_LIMIT) {
    RESPONSE_CACHE.delete(RESPONSE_CACHE.keys().next().value);
  }
  return request;
}

class SQuickAddModal extends HTMLElement {
  #ac;
  #fetchController;
  #dialog;
  #content;
  #status;
  #triggerElement;
  #productUrl = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#content = this.querySelector('[data-quick-add-content]');
    this.#status = this.querySelector('[data-quick-add-status]');

    if (!this.#dialog || !this.#content) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--open-product') this.#openProduct(event.source);
    }, opts);

    this.addEventListener('change', (event) => {
      this.#handleVariantChange(event);
    }, opts);

    // Keep the sheet open while the item is added, so the busy button and any cart error stay
    // visible; close it once the cart drawer opens on top.
    document.addEventListener('s-cart:opened', () => {
      if (this.#dialog?.open) this.#dialog.close();
    }, opts);

    // Warm the cache before the shopper commits: hovering, focusing or touching a card's quick-add
    // button prefetches its product, and hovering an option prefetches that variant.
    const prefetchTrigger = (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest(`[commandfor="${this.id}"][data-product-url]`)
        : null;
      if (trigger instanceof HTMLElement) this.#prefetch(trigger.dataset.productUrl);
    };
    document.addEventListener('pointerover', prefetchTrigger, { ...opts, passive: true });
    document.addEventListener('touchstart', prefetchTrigger, { ...opts, passive: true });
    document.addEventListener('focusin', prefetchTrigger, opts);

    this.addEventListener('pointerover', (event) => {
      if (!(event.target instanceof Element) || !this.#productUrl) return;
      const label = event.target.closest('label');
      const option = label?.control || event.target.closest('[data-variant-id]');
      const variantId = option instanceof HTMLElement ? option.dataset.variantId : '';
      if (variantId) this.#prefetch(this.#variantUrl(variantId));
    }, { ...opts, passive: true });

    this.#dialog.addEventListener('close', () => {
      this.#fetchController?.abort();
      this.removeAttribute('aria-busy');
      this.#restoreFocus();
    }, opts);

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
  }

  async #openProduct(source) {
    if (!(source instanceof HTMLElement)) return;

    const productUrl = source.dataset.productUrl;
    if (!productUrl) return;

    this.#triggerElement = source;
    this.#productUrl = productUrl;
    this.#setLoading();
    this.#showDialog();

    await this.#loadProduct(productUrl);
  }

  async #handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('[data-quick-add-variant-picker]');
    if (!picker || !this.contains(picker)) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || !this.#productUrl) return;

    this.#syncVariantInput(variantId);

    await this.#loadProduct(this.#variantUrl(variantId), { focusVariantId: variantId, keepContent: true });
  }

  #variantUrl(variantId) {
    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    return url.toString();
  }

  #prefetch(url) {
    if (!url) return;
    fetchProductHtml(new URL(url, window.location.origin).toString()).catch(() => {});
  }

  async #loadProduct(productUrl, { focusVariantId = null, keepContent = false } = {}) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;

    this.setAttribute('aria-busy', 'true');
    if (!keepContent) this.#setLoading();

    try {
      const responseText = await fetchProductHtml(new URL(productUrl, window.location.origin).toString());
      signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const quickAddSource = html.querySelector('[data-quick-add-source]');
      if (!quickAddSource) throw new Error('Quick add source was not found in product response.');

      const content = this.#prepareProductContent(quickAddSource);
      signal.throwIfAborted();

      this.#content.replaceChildren(content);
      this.#initializePaymentButtons();
      this.#announce('');
      this.removeAttribute('aria-busy');

      if (focusVariantId) this.#focusVariant(focusVariantId);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[s-quick-add-modal] Product fetch failed:', error);
      this.removeAttribute('aria-busy');
      this.#setError();
    } finally {
      if (this.#fetchController?.signal === signal) this.#fetchController = null;
    }
  }

  #prepareProductContent(source) {
    const content = source.cloneNode(true);

    content.className = 'flex flex-col gap-6';
    content.removeAttribute('data-quick-add-source');

    for (const script of content.querySelectorAll('script')) script.remove();
    content.querySelector('#inventory-status')?.remove();

    for (const picker of content.querySelectorAll('s-variant-picker')) {
      const replacement = document.createElement('div');
      for (const attribute of picker.attributes) {
        if (attribute.name === 'class') continue;
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.className = picker.className;
      replacement.setAttribute('data-quick-add-variant-picker', '');
      while (picker.firstChild) replacement.appendChild(picker.firstChild);
      picker.replaceWith(replacement);
    }

    return content;
  }

  #initializePaymentButtons() {
    if (!this.#content?.querySelector('.shopify-payment-button, [data-shopify-payment-button], shopify-accelerated-checkout')) return;

    window.Shopify?.PaymentButton?.init?.();
  }

  #showDialog() {
    if (!this.#dialog?.open) this.#dialog.showModal();
  }

  #setLoading() {
    const loadingText = this.dataset.loadingText || '';
    this.#announce(loadingText);
    this.#content?.replaceChildren(this.#messageNode(loadingText));
  }

  #setError() {
    const errorMessage = this.dataset.errorMessage || '';
    this.#announce(errorMessage);
    this.#content?.replaceChildren(this.#messageNode(errorMessage));
  }

  #messageNode(message) {
    const node = document.createElement('p');
    node.className = 'py-12 text-center text-sm text-on-surface-secondary';
    node.textContent = message;
    return node;
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #syncVariantInput(variantId) {
    const form = this.#content?.querySelector('form');
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  #focusVariant(variantId) {
    requestAnimationFrame(() => {
      for (const input of this.#content?.querySelectorAll('[data-variant-id]') || []) {
        if (input instanceof HTMLElement && input.dataset.variantId === variantId) {
          input.focus();
          return;
        }
      }
    });
  }

  #restoreFocus() {
    if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }
}

if (!customElements.get('s-quick-add-modal')) {
  customElements.define('s-quick-add-modal', SQuickAddModal);
}
